import React, { useState, useEffect } from 'react';
import { Divider, Flex, Spin } from 'antd';
import { CustomLogoIcon, StarIcon } from '@/assets/icons/icons';
import { previewOneTimeEmailNotification } from '@/apis/shipment';
import { useToken } from '@/common/helper';

interface DecodedToken {
  company_name?: string;
}

interface PrimaryContact {
  name?: string;
  email?: string;
}

interface Shipment {
  tp_primary_contact?: PrimaryContact;
  courier_tracking_id?: string;
}

interface EmailPreviewProps {
  decodedToken?: DecodedToken;
  shipment?: Shipment;
  customMessage?: string;
}

interface EmailPreviewResponseData {
  subject?: string;
  body_html?: string;
  body_text?: string;
}

interface EmailPreviewResponse {
  data?: {
    data?: {
      message?: string;
      status?: number;
      is_success?: boolean;
      data?: EmailPreviewResponseData;
    };
  };
}

/**
 * EmailPreview component that displays a preview of the email notification
 *
 * @param decodedToken - Token containing company information
 * @param shipment - Shipment data including contact information
 * @param customMessage - Custom message to include in the email
 */
const EmailPreview: React.FC<EmailPreviewProps> = ({
  decodedToken,
  shipment,
  customMessage,
}) => {
  const [emailContent, setEmailContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const { checkTokenExpiry } = useToken();

  useEffect(() => {
    if (!customMessage) {
      setEmailContent('');
      return;
    }

    const fetchEmailPreview = async () => {
      setLoading(true);
      try {
        const token = checkTokenExpiry() ?? '';
        const payload = {
          message: customMessage,
          contact_name: shipment?.tp_primary_contact?.name || 'Recipient',
        };
        const response = await previewOneTimeEmailNotification(token, payload);
        const bodyHtml =
          (response as EmailPreviewResponse)?.data?.data?.data?.body_html || '';
        const fullContent = bodyHtml
          ? `${bodyHtml}<br />List of selected shipments for this contact will appear here`
          : '';
        setEmailContent(fullContent);
      } catch (error) {
        console.error('Error fetching email preview:', error);
        setEmailContent('Failed to load email preview');
      } finally {
        setLoading(false);
      }
    };

    const _ = fetchEmailPreview();
  }, [customMessage, shipment?.tp_primary_contact?.name, checkTokenExpiry]);

  return (
    <>
      <div className='email-preview-wrapper'>
        <div className='blue-icon-box'>
          <StarIcon width={14} height={14} color='white' />
        </div>
        <div className='email-content-container'>
          <Flex gap={8}>
            <div className='logo-icon-box'>
              <CustomLogoIcon />
            </div>
            <Flex vertical gap={10} className='email-content'>
              <div>
                <Flex className='font-12 fw-500 lh-20'>
                  <h4>{decodedToken?.company_name}</h4>
                  <span className='font-10'>{`<customer@zoomlogi.com>`}</span>
                </Flex>
                <span className='font-10'>
                  {shipment?.tp_primary_contact?.email}
                </span>
              </div>
              <Divider className='notification-divider' />
              {loading ? (
                <Spin />
              ) : (
                <div
                  className='font-12 fw-400 lh-20'
                  dangerouslySetInnerHTML={{ __html: emailContent }}
                />
              )}
            </Flex>
          </Flex>
        </div>
      </div>
    </>
  );
};

export default EmailPreview;
